import PropTypes from "prop-types";

import Icon from "components/ShellV2/Icon";
import Button from "./Button";
import DateRangePopover from "./DateRangePopover";
import ValidityDateRangePicker from "./ValidityDateRangePicker";

/* Editable inline table for wizard-style forms (design's MiniTable).
   Column: { key, label, type?: "text"|"number"|"date"|"validity"|"select", options, width, ph,
   disabled?(row), getValue?(row), setValue?(row, value) -> row }. getValue/
   setValue let a column map onto nested fields (e.g. ownerships[0].member). */
export default function MiniTable({
  columns,
  rows,
  setRows,
  blank,
  addLabel,
  emptyLabel,
  removeLabel,
  max,
  onRemove,
  rowActions,
}) {
  const readCell = (col, row) => (col.getValue ? col.getValue(row) : row[col.key]) ?? "";

  const update = (i, col, value) =>
    setRows((rs) =>
      rs.map((r, j) => {
        if (j !== i) return r;
        return col.setValue ? col.setValue(r, value) : { ...r, [col.key]: value };
      })
    );

  const remove = (i) => {
    if (onRemove) onRemove(i, rows[i]);
    setRows((rs) => rs.filter((_, j) => j !== i));
  };

  return (
    <>
      <div className="rec-tbl-wrap rec-mini-table-wrap">
        <table className="rec-tbl rec-simtbl rec-mini-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} style={{ width: c.width }}>
                  {c.label}
                </th>
              ))}
              <th style={{ width: 44 }} aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr
                key={r.pre_id || i}
                data-column-count={columns.length}
                style={{ "--rec-mini-column-count": columns.length }}
              >
                {columns.map((c) => {
                  const disabled = c.disabled ? c.disabled(r) : false;
                  let control;
                  if (c.type === "select") {
                    control = (
                      <select
                        className="rec-input"
                        value={readCell(c, r)}
                        disabled={disabled}
                        onChange={(e) => update(i, c, e.target.value)}
                      >
                        {(typeof c.options === "function" ? c.options(r) : c.options).map((o) => {
                          const value = typeof o === "object" ? o.value : o;
                          const label = typeof o === "object" ? o.label : o;
                          return (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          );
                        })}
                      </select>
                    );
                  } else if (c.type === "validity") {
                    const validity = readCell(c, r);
                    control = (
                      <ValidityDateRangePicker
                        label={null}
                        value={{ start: validity.start || "", end: validity.end || "" }}
                        disabled={disabled}
                        onChange={(value) => update(i, c, value)}
                        portal
                      />
                    );
                  } else if (c.type === "date") {
                    control = (
                      <DateRangePopover
                        mode="single"
                        value={{ start: readCell(c, r), end: readCell(c, r) }}
                        disabled={disabled}
                        onChange={({ start }) => update(i, c, start)}
                        portal
                      />
                    );
                  } else {
                    control = (
                      <input
                        className="rec-input"
                        type={c.type === "number" ? c.type : "text"}
                        value={readCell(c, r)}
                        placeholder={c.ph || ""}
                        disabled={disabled}
                        onChange={(e) => update(i, c, e.target.value)}
                      />
                    );
                  }
                  return (
                    <td
                      key={c.key}
                      data-label={
                        typeof c.label === "string" || typeof c.label === "number"
                          ? c.label
                          : undefined
                      }
                    >
                      {control}
                    </td>
                  );
                })}
                <td className="rec-mini-table-actions">
                  {rowActions && rowActions(r, i)}
                  <button
                    type="button"
                    className="rec-icon-btn !w-[34px] !h-[34px]"
                    title={removeLabel}
                    onClick={() => remove(i)}
                  >
                    <Icon name="delete" style={{ fontSize: 18 }} />
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr className="rec-mini-table-empty-row">
                <td
                  className="rec-mini-table-empty"
                  colSpan={columns.length + 1}
                  style={{ textAlign: "center", padding: 24, color: "var(--ink-3)", fontSize: 13 }}
                >
                  {emptyLabel}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="mt-3"
        disabled={max ? rows.length >= max : false}
        onClick={() => setRows((rs) => [...rs, blank()])}
      >
        <Icon name="add" />
        {addLabel}
      </Button>
    </>
  );
}

MiniTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node,
      type: PropTypes.oneOf(["text", "number", "date", "validity", "select"]),
      options: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      ph: PropTypes.string,
      disabled: PropTypes.func,
      getValue: PropTypes.func,
      setValue: PropTypes.func,
    })
  ).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  rows: PropTypes.array.isRequired,
  setRows: PropTypes.func.isRequired,
  blank: PropTypes.func.isRequired,
  addLabel: PropTypes.node.isRequired,
  emptyLabel: PropTypes.node,
  removeLabel: PropTypes.string,
  max: PropTypes.number,
  onRemove: PropTypes.func,
  rowActions: PropTypes.func,
};

MiniTable.defaultProps = {
  emptyLabel: null,
  removeLabel: undefined,
  max: undefined,
  onRemove: undefined,
  rowActions: undefined,
};
